import type { UploadProgress } from '../type';
import { createProgressSnapshot } from './utils';

export interface HttpRequestAbortHandle {
  /** 请求创建后绑定的 XHR 实例，请求结束后会清空。 */
  instance: XMLHttpRequest | null;
}

/**
 * 直传和服务端上传共用的最小 XHR 请求参数。
 */
export interface HttpRequestOptions {
  method: 'get' | 'post' | 'put';
  url: string;
  data?: XMLHttpRequestBodyInit | null;
  xhrInstance?: HttpRequestAbortHandle;
  onProgress?: (progress: UploadProgress) => void;
}

/**
 * 保留 HTTP/XHR 细节的请求错误结构。
 */
export class HttpRequestError extends Error {
  readonly status?: number;
  readonly statusText?: string;
  readonly responseText?: string;
  readonly url: string;
  readonly isAborted: boolean;

  constructor(
    message: string,
    options: {
      url: string;
      status?: number;
      statusText?: string;
      responseText?: string;
      isAborted?: boolean;
    }
  ) {
    super(message);
    this.name = 'HttpRequestError';
    this.status = options.status;
    this.statusText = options.statusText;
    this.responseText = options.responseText;
    this.url = options.url;
    this.isAborted = Boolean(options.isAborted);
  }
}

/**
 * 执行 XMLHttpRequest，并统一处理进度、取消和失败路径。
 */
export function httpRequest(options: HttpRequestOptions): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    bindXhrInstance(options.xhrInstance, xhr);
    xhr.open(options.method, options.url, true);

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (options.onProgress && event.lengthComputable) {
        options.onProgress(createProgressSnapshot(event.loaded, event.total));
      }
    };

    xhr.onabort = () => {
      cleanup(options.xhrInstance, xhr);
      reject(
        new HttpRequestError('Request aborted', {
          url: options.url,
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText,
          isAborted: true,
        })
      );
    };

    xhr.onload = () => {
      cleanup(options.xhrInstance, xhr);

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText);
      } else {
        reject(
          new HttpRequestError(`Request failed with status ${xhr.status}`, {
            url: options.url,
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText,
          })
        );
      }
    };

    xhr.onerror = () => {
      cleanup(options.xhrInstance, xhr);
      reject(
        new HttpRequestError('Network error', {
          url: options.url,
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText,
        })
      );
    };

    xhr.send(options.data ?? null);
  });
}

function bindXhrInstance(
  handle: HttpRequestAbortHandle | undefined,
  xhr: XMLHttpRequest
): void {
  if (handle && typeof handle === 'object') {
    handle.instance = xhr;
  }
}

function cleanup(
  handle: HttpRequestAbortHandle | undefined,
  xhr: XMLHttpRequest
): void {
  if (handle?.instance === xhr) {
    handle.instance = null;
  }
}
